import { Assessment, QuestionPreview } from '$synapse/models';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { genericBaseUrl } from '$synapse/services';
import { ToastrService } from 'ngx-toastr';
import { AssessmentDTO, mapToAssessment } from 'src/app/dtos';
import { AssessmentRequestDTO } from '$synapse/dtos';
import { Pdf } from '../models/pdf';
import {
  QuestionPreviewDTO,
  mapToQuestionPreview,
} from '../dtos/question-preview-dto';

@Injectable({
  providedIn: 'root',
})
export class AssessmentService {
  private readonly _http = inject(HttpClient);
  private readonly _toastr = inject(ToastrService);

  baseUrl(...path: any[]): string {
    return genericBaseUrl(
      `${environment.settings.api.url}/manager/assessments`,
      ...path
    );
  }

  findAll(): Observable<Assessment[]> {
    return this._http
      .get<AssessmentDTO[]>(this.baseUrl())
      .pipe(map((assessments) => assessments.map(mapToAssessment)));
  }

  getById(id: string): Observable<Assessment> {
    return this._http
      .get<AssessmentDTO>(this.baseUrl(id))
      .pipe(map((assessment) => mapToAssessment(assessment)));
  }

  getQuestionsPreview(assessmentId: string): Observable<QuestionPreview[]> {
    return this._http
      .get<QuestionPreviewDTO[]>(this.baseUrl(assessmentId, 'questions'))
      .pipe(map((questions) => questions.map(mapToQuestionPreview)));
  }

  delete(id: string): Observable<any> {
    return this._http.delete<void>(this.baseUrl(id)).pipe();
  }

  uploadAssessment(
    assessmentRequest: AssessmentRequestDTO,
    assessmentExamFiles: FileList,
    pdfLessonFiles: FileList | null
  ): Observable<boolean> {
    if (assessmentExamFiles.length === 0) {
      return new Observable((subscriber) => subscriber.next(false));
    }
    const formData = new FormData();
    formData.append('csvFile', assessmentExamFiles.item(0));

    const pdfLessonFile = pdfLessonFiles?.item(0);
    if (pdfLessonFile) {
      formData.append('pdfLessonFile', pdfLessonFile);
    }

    formData.append(
      'assessmentRequest',
      new Blob([JSON.stringify(assessmentRequest)], {
        type: 'application/json',
      })
    );

    return this._http
      .post(this.baseUrl(), formData, { observe: 'response' })
      .pipe(
        map(() => {
          this._toastr.success(
            'Questionnaire enregistré avec succès',
            'Importation réussie'
          );
          return true;
        }),
        catchError((err: HttpErrorResponse) => {
          this._toastr.error(err.error.message, 'Une erreur est survenue');
          return of(false);
        })
      );
  }

  downloadLesson(assessmentId: string): Observable<Pdf> {
    return this._http.get<Pdf>(this.baseUrl() + `/${assessmentId}/pdf-lesson`);
  }

  downloadAssessmentCsv(assessmentId: string): Observable<Blob | null> {
    return this._http
      .get(this.baseUrl(assessmentId, 'csv'), {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          if (
            response.status === 204 ||
            !response.body ||
            response.body.size === 0
          ) {
            this._toastr.warning(
              'Aucun questionnaire disponible au téléchargement',
              'Aucun contenu'
            );
            return null;
          }
          this._toastr.success(
            'Questionnaire téléchargé avec succès',
            'Téléchargement réussi'
          );
          return response.body;
        }),
        catchError((error: HttpErrorResponse) => {
          this._toastr.error(
            'Aucun questionnaire disponible au téléchargement',
            'Une erreur est survenue'
          );
          return of(null);
        })
      );
  }

  certifyAssessment(assessmentId: string) {
    return this._http
      .post(this.baseUrl() + `/${assessmentId}`, {
        observe: 'response',
      })
      .pipe(
        map(() => {
          this._toastr.success(
            'Le questionnaire est maintenant certifiant',
            'Opération réussie'
          );
          return true;
        }),
        catchError((err: HttpErrorResponse) => {
          this._toastr.error(err.error.message, 'Une erreur est survenue');
          return of(false);
        })
      );
  }

  uploadAssessmentLesson(assessmentId: string, pdfLessonFiles: FileList) {
    if (pdfLessonFiles.length === 0) {
      return new Observable((subscriber) => subscriber.next(false));
    }
    const formData = new FormData();
    formData.append('pdfLessonFile', pdfLessonFiles.item(0));

    return this._http
      .post(this.baseUrl() + `/${assessmentId}/pdf-lesson`, formData, {
        observe: 'response',
      })
      .pipe(
        map(() => {
          this._toastr.success(
            'Cours enregistré avec succès',
            'Importation réussie'
          );
          return true;
        }),
        catchError((err: HttpErrorResponse) => {
          this._toastr.error(err.error.message, 'Une erreur est survenue');
          return of(false);
        })
      );
  }
}
